import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Space, Table, Typography, message as antdMessage } from "antd";
import { useTranslation } from "react-i18next";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { previewDesadv, generateDesadv } from "../../../../../functions/edifact";

const { Text } = Typography;

/**
 * Modal voor het genereren van een DESADV voor een order: toont per orderregel
 * het berekende aantal pallets (op basis van kratten-per-laag en de bundle-
 * samenstelling). Batchnummers worden door de backend automatisch bepaald
 * (product + verzenddatum — Growy oogst/verpakt/verzendt altijd dezelfde dag).
 * Op "Genereren" wordt de DESADV definitief aangemaakt (SSCC's worden hierbij
 * gereserveerd en kunnen niet meer hergebruikt worden) en op de order
 * opgeslagen — dit kan daarna niet meer herhaald worden voor dezelfde order
 * (de aanroeper moet de order eerst laten verwijderen om opnieuw te genereren).
 */
const GenerateDesadvModal = ({ visible, order, onCancel, onGenerated }) => {
  const { t } = useTranslation();
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!visible || !order) return;

    setPreview(null);
    setError(null);
    setLoadingPreview(true);

    previewDesadv(order.id, COMPANY_COUNTRY_CODE)
      .then((data) => setPreview(data))
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoadingPreview(false));
  }, [visible, order]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const { desadv, summary } = await generateDesadv(order.id, COMPANY_COUNTRY_CODE);
      antdMessage.success(t("desadv_generated_successfully"));
      onGenerated(desadv, summary);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const columns = [
    { title: t("product"), dataIndex: "productName", key: "productName" },
    { title: t("edifact_line_quantity"), dataIndex: "orderedQuantity", key: "orderedQuantity" },
    {
      title: t("desadv_pallet_count"),
      key: "palletCount",
      render: (_, line) => line.pallets.length,
    },
    {
      title: t("desadv_pallet_quantities"),
      key: "pallets",
      render: (_, line) => line.pallets.join(", "),
    },
  ];

  return (
    <Modal
      title={t("generate_desadv_title")}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t("cancel")}
        </Button>,
        <Button
          key="generate"
          type="primary"
          loading={generating}
          disabled={loadingPreview || !preview}
          onClick={handleGenerate}
        >
          {t("generate")}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Text type="secondary">{t("generate_desadv_message")}</Text>
        {preview?.lines
          ?.filter((line) => line.warning)
          .map((line) => (
            <Alert key={line.productId} type="warning" showIcon message={line.warning} />
          ))}
        <Table
          rowKey="productId"
          columns={columns}
          dataSource={preview?.lines || []}
          loading={loadingPreview}
          pagination={false}
          size="small"
        />
        {error && <Alert type="error" showIcon message={error} />}
      </Space>
    </Modal>
  );
};

export default GenerateDesadvModal;
